<template>
  <div v-if="plotItem">
    <div class="ai-chat-wrapper">
      <div class="ai-messages" ref="chatList">
        <!-- Welcome Message -->
        <div class="chat-row ai">
          <div class="" style="font-size: 14px">
            <div class="text" v-html="welcomeMessage"></div>
          </div>
        </div>
        <!-- Chat Messages -->
        <div v-for="(m, idx) in chatMessages" :key="idx" class="chat-row" :class="m.postedBy === 'user' ? 'user' : 'ai'">
          <div class="chat-box">
            <div class="posted-by">
              <div class="time"><span class="avatar" v-if="m.postedBy == 'ai'">🤖</span><span class="avatar" v-else>👤</span>{{ m.postedBy == "ai" ? "AI Assistant" : "You" }} &middot; {{ formatToShortDateTime(m.createdAt) }}</div>
            </div>
            <div class="chat-bubble" :class="m.postedBy">
              <div class="d-flex flex-column justify-content-between align-items-center mb-2" v-if="m.postedBy == 'ai' && m.messageMeta">
                <h3 style="color: grey; text-transform: uppercase">Topic: {{ m.messageMeta?.topic }}</h3>
                <small style="color: grey; text-transform: uppercase">Confidence: {{ m.messageMeta?.answerFoundInContextStatus == "ANSWERED_FOUND_IN_CONTEXT" ? "HIGH" : "LOW" }}</small>
              </div>
              <div class="text" v-html="m.message" @click="handleMessageClick($event)"></div>
              <div v-if="m.inputMeta" class="mb-2">
                <h4>Referenced Sections</h4>
                <div style="display: flex; flex-wrap: wrap; gap: 6px">
                  <template v-for="(item, index) in m.inputMeta.federalSearchResults">
                    <v-chip small color="blue lighten-4" text-color="blue darken-4" class="" :key="'federal_' + index" label title="Federal References">
                      <v-icon left small color="blue darken-2">mdi-domain</v-icon>
                      {{ item.payload.title || "Federal result" }}
                    </v-chip>
                  </template>
                  <template v-for="(item, index) in m.inputMeta.cantonSearchResults">
                    <v-chip small color="green lighten-4" text-color="green darken-4" class="" :key="'canton_' + index" label title="Canton References">
                      <v-icon left small color="green darken-2">mdi-map-marker</v-icon>
                      {{ item.payload.title || "Canton result" }}
                    </v-chip>
                  </template>
                  <template v-for="(item, index) in m.inputMeta.municipalSearchResults">
                    <v-chip small color="orange lighten-4" text-color="orange darken-4" class="" :key="'municipal_' + index" label title="Municipality References">
                      <v-icon left small color="orange darken-2">mdi-city</v-icon>
                      {{ item.payload.title || "Municipality result" }}
                    </v-chip>
                  </template>
                </div>
              </div>

              <div class="chat-meta" v-if="m.aiResponseMeta && m.aiResponseMeta.tokensUsed">
                Tokens used: <b>{{ Number(m.aiResponseMeta?.tokensUsed || 0).toLocaleString() }}</b>
              </div>
              <div v-if="m.inputMeta">
                <v-btn small text color="primary" @click="m.showMeta = true">Debug</v-btn>
                <v-dialog v-model="m.showMeta" max-width="600px">
                  <v-card>
                    <v-card-title>
                      Debug Info
                      <v-spacer></v-spacer>
                      <v-btn icon @click="m.showMeta = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <ul>
                        <li v-for="(item, index) of m.inputMeta?.federalSearchResults" :key="'CC_' + index"><strong>Federal Title:</strong> {{ item.payload.title }}</li>
                        <li v-for="(item, index) of m.inputMeta?.cantonSearchResults" :key="'C_' + index"><strong>Canton:</strong> {{ item.payload.title }}</li>
                        <li v-for="(item, index) of m.inputMeta?.municipalSearchResults" :key="'M_' + index"><strong>Municipality:</strong> {{ item.payload.title }}</li>
                      </ul>
                      <pre style="white-space: pre-wrap; font-size: 13px">{{ JSON.stringify(m.inputMeta, null, 2) }}</pre>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </div>
            </div>
          </div>
        </div>
        <!-- Typing indicator -->
        <div v-if="typing" class="chat-row ai">
          <div class="chat-bubble ai typing-indicator">
            <span v-if="typingText" class="orange--text"
              ><b>{{ typingText }}</b></span
            >
            <span class="dots">
              <span v-for="n in 3" :key="n" :class="'dot dot-' + n"></span>
            </span>
          </div>
        </div>
      </div>
      <div class="ai-input-wrapper">
        <v-textarea v-model="chatInput" rounded append-outer-icon="mdi-send" filled clear-icon="mdi-close-circle" placeholder="Ask Me Anything" type="text" auto-grow rows="1" class="chat-input" @click:append-outer="sendChat" @keydown.enter.exact.prevent="sendChat"></v-textarea>
      </div>

      <v-dialog v-model="showRefDialog" max-width="500">
        <v-card>
          <v-card-title
            >Reference Detail
            <v-spacer></v-spacer>
            <v-btn icon @click="showRefDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-4">
            {{ selectedRefContent }}
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from "vue-property-decorator"
import {config, utils} from "@/libs/utils"
import {chatService} from "./chat-service"

@Component({
  components: {},
})
export default class Chat extends Vue {
  @Prop()
  plotItem

  chatInput = ""
  chatMessages: any = []
  welcomeMessage = ""
  typing = false
  typingText = "AI is typing"
  showRefDialog = false
  selectedRefContent = ""
  jobID = 0

  processMessage(m: any) {
    if (!m.message || !m.messageMeta || !m.messageMeta.references_array) return m

    let msg = m.message
    const refs = m.messageMeta.references_array

    // Regex to find [REF#:'...']
    const regex = /\[REF#:'(.*?)'\]/g

    msg = msg.replace(regex, (match, refKey) => {
      // Find the full reference content in the array
      // Try exact match first
      let content = refs.find((r) => r === refKey)

      // If not found, try to find if refKey is contained in one of the refs
      if (!content) {
        content = refs.find((r) => r.includes(refKey))
      }

      if (content) {
        // Escape content for HTML attribute
        const escapedContent = content.replace(/"/g, "&quot;")
        return `<span class="ref-link" data-content="${escapedContent}">${refKey}</span>`
      }
      return match
    })

    m.message = msg
    return m
  }

  handleMessageClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (target && target.classList.contains("ref-link")) {
      const content = target.getAttribute("data-content")
      if (content) {
        this.selectedRefContent = content
        this.showRefDialog = true
      }
    }
  }

  private isScrollAtBottom = true

  created() {
    this.welcomeMessage = `Hello! I'm your AI assistant. Ask me anything about this plot: <b>${this.plotItem ? this.plotItem.PRC_ID : ""}</b>.<p style="color: #888;line-height:1.3;font-size:12px;"><i>Disclaimer: AI-generated answers may not always be accurate or complete. Please verify any important information independently.</i></p>
    
    `
    this.loadChat()
  }

  formatToShortDateTime(dateStr: string) {
    let date = new Date(dateStr)
    return date.toLocaleString(config.locale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }
  mounted() {
    this.$nextTick(() => {
      this.scrollToBottom()
      this.attachScrollListener()
    })
  }

  beforeDestroy() {
    this.detachScrollListener()
    if (this.jobID) {
      console.log("Clearing jobID on component destroy", this.jobID)
      clearInterval(this.jobID)
      this.jobID = 0
    }
  }

  attachScrollListener() {
    const el = this.$refs.chatList as HTMLElement
    if (el) {
      el.addEventListener("scroll", this.handleScroll)
    }
  }

  detachScrollListener() {
    const el = this.$refs.chatList as HTMLElement
    if (el) {
      el.removeEventListener("scroll", this.handleScroll)
    }
  }

  handleScroll = () => {
    const el = this.$refs.chatList as HTMLElement
    if (!el) return
    // Consider at bottom if within 10px of the bottom
    this.isScrollAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10
  }

  scrollToBottom() {
    const el = this.$refs.chatList as HTMLElement
    if (el) el.scrollTop = el.scrollHeight
  }

  async loadChatPoling() {
    this.jobID = setInterval(async () => {
      await this.loadChat()

      let lastMessage = this.chatMessages[this.chatMessages.length - 1]
      if (lastMessage?.postedBy === "ai" && this.jobID) {
        console.log("AI response received, clearing jobID interval", this.jobID)
        clearInterval(this.jobID)
        this.jobID = 0
        this.typing = false
      }
    }, 5000)
  }

  async loadChat() {
    let chatMessages = await chatService.loadChat(this.plotItem.PRC_ID)
    console.log("chatMessages", chatMessages)
    this.chatMessages = chatMessages.messages || []
    // Use Vue.set for reactivity
    this.chatMessages.forEach((m: any, idx: number) => {
      m = this.processMessage(m)
      this.$set(this.chatMessages, idx, {...m, showMeta: false})
    })
    this.$nextTick(() => {
      if (this.isScrollAtBottom) this.scrollToBottom()
    })
  }

  async sendChat() {
    if (this.jobID) {
      console.log("Clearing existing jobID", this.jobID)
      clearInterval(this.jobID)
      this.jobID = 0
    }
    console.log("sendChat")
    const text = (this.chatInput || "").trim()
    if (!text) return
    console.log("text", text)
    const now = new Date().toISOString()
    const userMsg = {postedBy: "user", message: text, createdAt: now}
    this.chatMessages.push(userMsg)
    // Ensure showMeta is reactive
    this.$set(this.chatMessages[this.chatMessages.length - 1], "showMeta", false)
    this.chatInput = ""
    this.$nextTick(() => {
      if (this.isScrollAtBottom) this.scrollToBottom()
    })

    // Show typing indicator
    this.typingText = "Generating response"
    this.typing = true

    let chatRes = await chatService.sendChat(this.plotItem.PRC_ID, text)
    console.log("chatRes", chatRes)
    if (chatRes?.error) {
      this.typing = false
      const errorMsg = {postedBy: "ai", message: `<span style="color:red;"><b>Error:</b> ${chatRes.error}</span>`, createdAt: new Date().toISOString()}
      this.chatMessages.push(errorMsg)
      this.$set(this.chatMessages[this.chatMessages.length - 1], "showMeta", false)
      this.$nextTick(() => {
        if (this.isScrollAtBottom) this.scrollToBottom()
      })
      return
    }
    let aiMsg = chatRes.aiMsg
    // Hide typing indicator

    if (!aiMsg) {
      if (chatRes.jobID) {
        console.log("Setting up jobID for pooling", chatRes.jobID)
        this.loadChatPoling()
      } else {
        console.log("WARNING", "No AI message received")
        this.typing = false
      }
      return
    }
    this.typing = false
    aiMsg = this.processMessage(aiMsg)
    this.chatMessages.push(aiMsg)
    // Ensure showMeta is reactive for AI message
    this.$set(this.chatMessages[this.chatMessages.length - 1], "showMeta", false)
    this.$nextTick(() => {
      if (this.isScrollAtBottom) this.scrollToBottom()
    })
  }
}
</script>

<style>
/* ChatGPT-like Chat Layout */
.ai-chat-wrapper {
  position: relative;
  flex: 1 1 auto;
  height: 75vh; /* changed from 100% */
  display: flex;
  flex-direction: column; /* ensure vertical stacking */
  /* overflow: auto; removed */
}

.ai-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  width: 100%;
  padding: 24px 0 50px 0;
  scroll-behavior: smooth;
  /* overflow: auto; removed */
  min-height: 0; /* allow flexbox to work properly */
  max-height: calc(100vh - 220px); /* ensure it doesn't overflow input */
}

.ai-input-wrapper {
  position: fixed;
  left: 50%;
  bottom: 0px;
  transform: translateX(-50%);
  width: calc(100% - 48px);
  max-width: 680px;
  background: #fff;
  border-radius: 16px;
  padding: 16px 20px 14px;
  display: flex;
  align-items: flex-end;
  z-index: 10; /* ensure input stays above messages */
}

.ai-input-wrapper .v-textarea.chat-input .v-text-field__slot textarea {
  font-size: 16px;
  line-height: 1.5;
  max-height: 180px;
  overflow-y: auto;
  align-self: center;
  margin-left: 20px;
  background: transparent;
  color: #222;
}

.chat-row {
  display: flex;
  width: 100%;
  margin-bottom: 18px;
}
.chat-meta {
  font-size: 11px;
  opacity: 0.7;
  margin-bottom: 4px;
}

.chat-row.user {
  justify-content: flex-end;
}

.chat-row.ai {
  justify-content: flex-start;
}

.avatar {
  margin-right: 6px;
}

.posted-by {
  margin-bottom: 4px;
  font-size: 12px;
  color: #555;
}

.chat-box {
  position: relative;
  max-width: 70%;
  padding: 16px 0px 12px;
  font-size: 15px;
  line-height: 1.6;
  transition: background 0.2s;
}
.chat-bubble {
  padding: 16px 20px 12px;
  line-height: 1.6;
  border-radius: 16px;
  word-break: break-word;
  /* white-space: pre-wrap; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: background 0.2s;
}

.chat-bubble.user {
  background: linear-gradient(135deg, #092d31 0%, #1a7a84 64%, #2199a5 100%);
  color: #fff;
  border-bottom-right-radius: 6px;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
  box-shadow: 0 2px 8px rgba(16, 163, 127, 0.08);
}

.chat-bubble.ai {
  background: #fff;
  color: #222;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 6px;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  border-bottom-right-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.chat-bubble .text {
  display: block;
  margin-bottom: 6px;
}

.chat-bubble .time {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.55;
  text-align: right;
  letter-spacing: 0.3px;
}

.chat-bubble.user .time {
  color: rgba(255, 255, 255, 0.75);
}

.chat-bubble.ai .time {
  color: #4b5563;
}

/* Typing indicator styles */
.typing-indicator {
  display: flex;
  align-items: center;
  min-height: 32px;
  font-size: 15px;
  background: #fff;
  color: #222;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.typing-indicator .dots {
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
}

.typing-indicator .dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin: 0 2px;
  border-radius: 50%;
  background: #442706;
  opacity: 0.6;
  animation: blink 1.4s infinite both;
}

.typing-indicator .dot-1 {
  animation-delay: 0s;
}
.typing-indicator .dot-2 {
  animation-delay: 0.2s;
}
.typing-indicator .dot-3 {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0.6;
  }
  40% {
    opacity: 1;
  }
}

/* Smooth scrollbar aesthetics */
.ai-messages::-webkit-scrollbar {
  width: 8px;
}
.ai-messages::-webkit-scrollbar-track {
  background: transparent;
}
.ai-messages::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.ai-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.22);
}

/* Responsive adjustments */
@media (max-width: 700px) {
  .ai-input-wrapper {
    max-width: 98vw;
    left: 1vw;
    transform: none;
  }
  .ai-chat-wrapper {
    border-radius: 8px;
    height: 100vh; /* ensure mobile uses full viewport */
  }
  .ai-messages {
    padding: 12px 0 120px 0;
    max-height: calc(100vh - 140px); /* adjust for mobile input height */
  }
  .chat-bubble {
    font-size: 14px;
    padding: 12px 14px 10px;
  }
}

.text p {
  margin: 18px 0 6px 0;
  line-height: 1.36;
}

.ref-link {
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  position: relative;
  font-weight: 500;
}

.ref-link:hover::after {
  content: attr(data-content);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: normal;
  width: max-content;
  max-width: 300px;
  z-index: 100;
  pointer-events: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  margin-bottom: 5px;
}
</style>
